import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/AuthContext";
import { Seo } from "./components/Seo";
import { AgentGuide } from "./components/AgentGuide";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import Founder from "./pages/Founder";
import Reviews from "./pages/Reviews";
import Company from "./pages/Company";
import Contact from "./pages/Contact";
import Investors from "./pages/Investors";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";
import Billing from "./pages/Billing";
import Media from "./pages/Media";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import NotFound from "./pages/NotFound";

const ROUTE_SEO = {
    "/": { title: "WorkAssign AI — AI Receptionist & AI Calling Agent for Small Business India", description: "24×7 AI receptionist, AI calling agent and WhatsApp automation for Indian small businesses — pay-as-you-go from ₹499. No mandatory monthly bills." },
    "/pricing": { title: "Pricing — Pay-As-You-Go AI Assistant & Enterprise AI Workforce | WorkAssign AI", description: "Small Business prepaid recharge at ₹499 (AI calling minutes or WhatsApp messages) and Enterprise Premium AI workforce at ₹40,000/month. Transparent billing, no hidden charges." },
    "/company": { title: "About Us — Virox WorkAssign Technology | WorkAssign AI", description: "WorkAssign AI builds AI employees that call, chat, manage, follow up and automate — so businesses grow without growing headcount." },
    "/founder": { title: "Founder Journey — Dharmendra Veer Silawat | WorkAssign AI", description: "The story behind WorkAssign AI — 12+ years across banking, hospitality and technology, and the vision for India's AI workforce." },
    "/reviews": { title: "Customer Reviews | WorkAssign AI", description: "What businesses say about their WorkAssign AI employees — AI calling and WhatsApp automation in action." },
    "/media": { title: "Media & Press Coverage | WorkAssign AI", description: "WorkAssign AI's initiative to bring AI automation to small businesses — covered by publications across India." },
    "/blog": { title: "Blog — AI, Automation & Business Growth | WorkAssign AI", description: "Ideas on AI calling agents, WhatsApp automation and AI customer support for growing businesses." },
    "/contact": { title: "Contact Us | WorkAssign AI", description: "Talk to the WorkAssign AI team — book a demo of your AI employee, ask about plans, or reach us on WhatsApp." },
    "/investors": { title: "For Investors | WorkAssign AI", description: "Investor information for WorkAssign AI — India's AI workforce platform for businesses." },
    "/terms": { title: "Terms & Conditions | WorkAssign AI", description: "Terms and conditions for using WorkAssign AI services." },
    "/privacy": { title: "Privacy Policy | WorkAssign AI", description: "How WorkAssign AI collects, uses and protects your business and customer data." },
    "/refund": { title: "Refund & Cancellation Policy | WorkAssign AI", description: "Refund and cancellation policy for WorkAssign AI recharges and subscriptions." },
    "/billing": { title: "Billing & Usage Policy | WorkAssign AI", description: "How billing, prepaid recharge and usage metering work at WorkAssign AI — no enquiry, no usage charge." },
};

const RouteSeo = () => {
    const { pathname } = useLocation();
    const meta = ROUTE_SEO[pathname] || (pathname.startsWith("/blog") ? ROUTE_SEO["/blog"] : ROUTE_SEO["/"]);
    return <Seo title={meta.title} description={meta.description} path={pathname} />;
};

const ScrollToTop = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 350);
        } else {
            window.scrollTo(0, 0);
        }
    }, [pathname, hash]);
    return null;
};

const Shell = ({ children }) => {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/admin");
    return (
        <>
            <ScrollToTop />
            {!isAdmin && <Navbar />}
            <main className={isAdmin ? "" : "pt-16"}>{children}</main>
            {!isAdmin && <AgentGuide />}
            {!isAdmin && <Footer />}
        </>
    );
};

function App() {
    return (
        <div className="App min-h-screen bg-[#080C14] text-slate-200">
            <AuthProvider>
                <HelmetProvider>
                    <BrowserRouter>
                        <Shell>
                            <RouteSeo />
                            <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/pricing" element={<Pricing />} />
                            <Route path="/founder" element={<Founder />} />
                            <Route path="/reviews" element={<Reviews />} />
                            <Route path="/company" element={<Company />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/investors" element={<Investors />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/refund" element={<Refund />} />
                            <Route path="/billing" element={<Billing />} />
                            <Route path="/media" element={<Media />} />
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Shell>
                    </BrowserRouter>
                </HelmetProvider>
            </AuthProvider>
            <Toaster position="top-center" theme="dark" richColors />
        </div>
    );
}

export default App;
